// ============================================================
// CourtApproval.tsx — (B4, batch 2 read-first) a child's / protected party's
// court approval on the statement:
//   • the court order: a one-line summary once recorded ("✓ Court order
//     recorded 29 Oct 2026 · County Court at Manchester · £5,000.00
//     approved") with a pencil; before that a "Record the court order" button
//     opens the form (Save waits while a money box holds invalid text — M11;
//     leaving with unsaved changes asks first — M16);
//   • the court steps: a visual tracker (Agreed → … → Deductions released),
//     each step a button — click it to record it with a date (skipping
//     earlier steps is allowed); "Record refused" / "Record adjourned" are
//     secondary; the history stays underneath, compact.
// (Audit B2, H3) The approved damages AND the approved deductions are
// required (the statement is checked against them before it may say the
// court approved it). A corrected version starts with no order: the form
// opens filled from the previous version's order, to check and record again.
// The court steps carry id #court-steps (a "Record the court's decision"
// blocker focuses them).
// ============================================================
import React, { useId, useRef, useState } from 'react'
import type { recordCourtOrderApi } from '../../../api/casevoApi'
import type { CaseStatements, CourtStage, StatementDraft, StatementSummary } from '../../../types/settlementStatement.types'
import { B_PRIMARY, B_SECONDARY, B_SECONDARY_SM, BIcon, INPUT, LABEL, NOTE, money } from '../../billing/billingUi'
import { PenceField, useBadFields } from '../settlementUi'
import DateInput from '../../shared/DateInput'
import FormField from '../../shared/FormField'
import FieldError from '../../shared/FieldError'
import { useUnsavedChangesGuard } from '../../../hooks/useUnsavedChangesGuard'
import { ukDate } from '../../../utils/civilDate'
import { EditPencil } from './statementBits'

export type RecordOrder = (body: Parameters<typeof recordCourtOrderApi>[2]) => Promise<boolean>
export type RecordStage = (body: { stage: CourtStage; on: string; note?: string }) => Promise<boolean>

// The first field of the court-order form (a blocker's button focuses it).
export const COURT_ORDER_FIRST_FIELD = 'statement-court-order-date'
export const COURT_STEPS_ID = 'court-steps'
const SELECT = `${INPUT} pr-8`

// ── The court order ─────────────────────────────────────────
export const CourtOrderPanel: React.FC<{
  draft: StatementDraft; stages: CaseStatements['courtStages']; busy: string | null; today: string
  open: boolean; setOpen: (o: boolean) => void
  // Prepared / approved: the order can't change (send it back first).
  locked: boolean
  // The "Before it can be issued" box already carries "Record the court order".
  hideOpenButton: boolean
  recordOrder: RecordOrder; recordStage: RecordStage
  // (Audit B2, H3) The previous version's order (a correction) — the form starts from it.
  previousOrder?: StatementSummary['courtOrder']
}> = ({ draft, stages, busy, today, open, setOpen, locked, hideOpenButton, recordOrder, recordStage, previousOrder }) => {
  const o = draft.courtOrder
  return (
    <section aria-label="Court approval" id="court-order" className="rounded-[12px] border border-casevo-border px-[14px] py-[12px] flex flex-col gap-3">
      <div>
        <h4 className="m-0 text-[14px] font-semibold text-casevo-forest">Court approval</h4>
        <p className={`${NOTE} m-0`}>A {draft.capacity === 'CHILD' ? 'child’s' : 'protected party’s'} settlement and any deductions need the court’s approval (CPR 21.10). Record the order here; the deductions follow the approved figure.</p>
      </div>
      {open ? (
        <CourtOrderForm draft={draft} previousOrder={previousOrder ?? null} busy={busy} recordOrder={recordOrder} onDone={() => setOpen(false)} />
      ) : o ? (
        <div className="flex items-center gap-[8px] flex-wrap" data-testid="court-order-summary">
          <p className="m-0 flex-1 min-w-0 text-[13.5px] text-casevo-forest">
            <span className="text-casevo-brand dark:text-casevo-link font-semibold" aria-hidden="true">✓ </span>
            Court order recorded {ukDate(o.orderDate)} · {o.courtName}{o.approvedDamagesPence != null ? ` · damages ${money(o.approvedDamagesPence)}` : ''}{o.approvedDeductionsPence != null ? ` · deductions ${money(o.approvedDeductionsPence)} approved` : ''}
            {o.claimNumber ? <span className="text-casevo-text-soft"> · claim {o.claimNumber}</span> : null}
          </p>
          {!locked && <EditPencil label="Edit the court order" disabled={!!busy} onClick={() => setOpen(true)} />}
        </div>
      ) : previousOrder && !hideOpenButton ? (
        <div className="flex items-center gap-[8px] flex-wrap">
          <p className={`${NOTE} m-0 flex-1 min-w-0`}>This is a new version, so the court order is recorded on it again and checked against these figures.</p>
          <button type="button" className={B_SECONDARY} disabled={!!busy || locked} onClick={() => setOpen(true)}>Record the court order</button>
        </div>
      ) : hideOpenButton ? (
        <p className={`${NOTE} m-0`}>No court order recorded yet — see “Before it can be issued”.</p>
      ) : (
        <div className="flex items-center gap-[8px] flex-wrap">
          <button type="button" className={B_SECONDARY} disabled={!!busy || locked} onClick={() => setOpen(true)}>Record the court order</button>
          {locked && <span className={NOTE}>Send it back for changes first.</span>}
        </div>
      )}
      <CourtSteps stages={stages} history={draft.courtStageHistory} current={draft.courtStage} busy={busy} today={today} onRecord={recordStage} />
    </section>
  )
}

const CourtOrderForm: React.FC<{ draft: StatementDraft; previousOrder: StatementSummary['courtOrder']; busy: string | null; recordOrder: RecordOrder; onDone: () => void }> = ({ draft, previousOrder, busy, recordOrder, onDone }) => {
  const id = useId()
  const o = draft.courtOrder ?? previousOrder
  // (Audit fix batch 2, Low 15) Frozen when the form opens: a background
  // reload (a new courtOrder object) doesn't flip its unsaved state.
  const [initial] = useState(() => ({
    orderDate: o?.orderDate ?? null as string | null, courtName: o?.courtName ?? '', claimNumber: o?.claimNumber ?? '', judgeName: o?.judgeName ?? '',
    approvedDamagesPence: o?.approvedDamagesPence ?? null as number | null, approvedDeductionsPence: o?.approvedDeductionsPence ?? null as number | null,
    costsAssessment: o?.costsAssessment ?? null as 'SUMMARY' | 'DETAILED' | null,
  }))
  const [f, setF] = useState(initial)
  const [tried, setTried] = useState(false)
  const bad = useBadFields()
  const dirty = JSON.stringify(f) !== JSON.stringify(initial)
  const guard = useUnsavedChangesGuard(dirty)
  const errs = {
    orderDate: !f.orderDate ? 'Enter the date of the order.' : null, courtName: !f.courtName.trim() ? 'Enter the court.' : null,
    damages: f.approvedDamagesPence == null ? 'Enter the damages the court approved, as stated in the order.' : null,
    deductions: f.approvedDeductionsPence == null ? 'Enter the deductions the court approved, as stated in the order (£0 if none).' : null,
  }
  const submit = async (): Promise<void> => {
    setTried(true)
    if (errs.orderDate || errs.courtName || errs.damages || errs.deductions || bad.any) return
    if (await recordOrder({ ...f, orderDate: f.orderDate as string })) onDone()
  }
  const label = draft.courtOrder ? 'Save the court order' : 'Record the court order'
  return (
    <div className="flex flex-col gap-3">
      <div className="grid gap-[12px] sm:grid-cols-2">
        <FormField label="Order date" required variant="info">
          <div id={COURT_ORDER_FIRST_FIELD}>
            <DateInput value={f.orderDate} onChange={(v) => setF({ ...f, orderDate: v })} ariaLabel="Order date" allowFuture={false} />
          </div>
          {tried && <FieldError message={errs.orderDate ?? undefined} />}
        </FormField>
        <FormField label="Court" required variant="info" htmlFor={`${id}-court`}>
          <input id={`${id}-court`} className={INPUT} value={f.courtName} maxLength={200} onChange={(e) => setF({ ...f, courtName: e.target.value })}
            aria-invalid={(tried && !!errs.courtName) || undefined} aria-describedby={tried && errs.courtName ? `${id}-court-err` : undefined} />
          <span id={`${id}-court-err`}>{tried && <FieldError message={errs.courtName ?? undefined} />}</span>
        </FormField>
        <FormField label="Claim number" variant="info" htmlFor={`${id}-claim`}>
          <input id={`${id}-claim`} className={INPUT} value={f.claimNumber} maxLength={60} onChange={(e) => setF({ ...f, claimNumber: e.target.value })} />
        </FormField>
        <FormField label="Judge" variant="info" htmlFor={`${id}-judge`}>
          <input id={`${id}-judge`} className={INPUT} value={f.judgeName} maxLength={200} onChange={(e) => setF({ ...f, judgeName: e.target.value })} />
        </FormField>
        <PenceField label="Damages approved" pence={f.approvedDamagesPence} allowBlank onPence={(p) => setF({ ...f, approvedDamagesPence: p })} onBadChange={bad.track('damages')}
          error={tried ? errs.damages : null} hint="Required — as stated in the order." />
        <PenceField label="Deductions approved" pence={f.approvedDeductionsPence} allowBlank onPence={(p) => setF({ ...f, approvedDeductionsPence: p })} onBadChange={bad.track('deductions')}
          error={tried ? errs.deductions : null} hint="Required — £0 if the court approved none." />
        <FormField label="Costs assessment" variant="info" htmlFor={`${id}-assess`}>
          <select id={`${id}-assess`} className={SELECT} value={f.costsAssessment ?? ''} onChange={(e) => setF({ ...f, costsAssessment: (e.target.value || null) as 'SUMMARY' | 'DETAILED' | null })}>
            <option value="">Not stated</option><option value="SUMMARY">Summary</option><option value="DETAILED">Detailed</option>
          </select>
        </FormField>
      </div>
      <div className="flex items-center gap-[10px] flex-wrap">
        <button type="button" className={B_PRIMARY} disabled={!!busy || bad.any} onClick={() => { void submit() }}>{busy === 'order' ? 'Saving…' : label}</button>
        <button type="button" className={B_SECONDARY} onClick={() => guard(onDone)}>Cancel</button>
        {bad.any && <span className={NOTE}>Correct the amounts first.</span>}
      </div>
    </div>
  )
}

// ── The court steps: a tracker ───────────────────────────────
const TRACK: { key: CourtStage; label: string }[] = [
  { key: 'AGREED_SUBJECT_TO_APPROVAL', label: 'Agreed' },
  { key: 'APPLICATION_ISSUED', label: 'Application issued' },
  { key: 'LISTED', label: 'Listed' },
  { key: 'APPROVED', label: 'Approved' },
  { key: 'ORDER_SEALED', label: 'Order sealed' },
  { key: 'PAID_TO_CFO', label: 'Paid to CFO' },
  { key: 'DEDUCTIONS_RELEASED', label: 'Deductions released' },
]
const SIDE: Partial<Record<CourtStage, string>> = { REFUSED: 'Refused', ADJOURNED: 'Adjourned' }

export const CourtSteps: React.FC<{
  stages: CaseStatements['courtStages']; history: StatementSummary['courtStageHistory']; current: CourtStage | null
  busy: string | null; today: string; onRecord: RecordStage
}> = ({ stages, history, current, busy, today, onRecord }) => {
  const id = useId()
  const [pick, setPick] = useState<{ stage: CourtStage; on: string | null; note: string } | null>(null)
  const label = (k: CourtStage): string => TRACK.find((t) => t.key === k)?.label ?? SIDE[k] ?? stages.find((s) => s.key === k)?.label ?? k
  const lastOn = new Map<CourtStage, string>()
  history.forEach((h) => lastOn.set(h.stage, h.on))
  const currentIdx = TRACK.findIndex((t) => t.key === current)
  // (Audit fix batch 2, Low 15) The "Record …" form asks before its typing is
  // dropped (UnsavedGuard) and takes focus when it opens; Cancel goes back to
  // the step that opened it.
  const opener = useRef<string | null>(null)
  const stepDirty = !!pick && (pick.note.trim() !== '' || pick.on !== today)
  const guardStep = useUnsavedChangesGuard(stepDirty && busy !== 'stage')
  const open = (stage: CourtStage): void => guardStep(() => {
    opener.current = (document.activeElement as HTMLElement | null)?.id || null
    setPick({ stage, on: today, note: '' })
    window.setTimeout(() => document.getElementById(`${id}-step-day`)?.closest('[role=group]')?.querySelector<HTMLInputElement>('input')?.focus(), 0)
  })
  const cancelStep = (): void => guardStep(() => {
    setPick(null)
    const back = opener.current
    window.setTimeout(() => { if (back) document.getElementById(back)?.focus() }, 0)
  })
  const record = async (): Promise<void> => {
    if (!pick || !pick.on) return
    if (await onRecord({ stage: pick.stage, on: pick.on, note: pick.note })) setPick(null)
  }
  return (
    <div className="flex flex-col gap-2">
      <ol id={COURT_STEPS_ID} tabIndex={-1} aria-label="Court approval steps" className="m-0 p-0 list-none flex flex-wrap items-stretch gap-[6px]">
        {TRACK.map((t, i) => {
          const on = lastOn.get(t.key)
          const isCurrent = t.key === current
          const passed = !!on || (currentIdx >= 0 && i <= currentIdx)
          return (
            <li key={t.key} className="flex items-center gap-[6px]">
              <button type="button" id={`${id}-step-${t.key}`} aria-current={isCurrent ? 'step' : undefined} disabled={!!busy} onClick={() => open(t.key)}
                className={`inline-flex items-center gap-[6px] rounded-[8px] border px-[8px] py-[4px] text-[12.5px] focus:outline-none casevo-focus disabled:opacity-60 ${isCurrent ? 'border-casevo-brand bg-casevo-mint font-semibold text-casevo-forest' : passed ? 'border-casevo-brand/40 text-casevo-forest' : 'border-casevo-border text-casevo-text-soft hover:border-casevo-mid'}`}>
                <span className={`w-[16px] h-[16px] rounded-full grid place-items-center border ${passed ? 'bg-casevo-brand border-casevo-brand text-white' : 'border-casevo-border'}`} aria-hidden="true">{passed && <BIcon name="check" size={10} strokeWidth={3} />}</span>
                <span>{t.label}</span>
                {on && <span className="font-normal text-casevo-text-soft tabular-nums">{ukDate(on)}</span>}
              </button>
              {/* The arrow trails its step, so a wrapped row never starts with one. */}
              {i < TRACK.length - 1 && <span className="text-casevo-text-soft" aria-hidden="true">→</span>}
            </li>
          )
        })}
      </ol>
      <div className="flex items-center gap-[8px] flex-wrap">
        {current && SIDE[current] && <span className="text-[12.5px] font-semibold text-casevo-warn">Last step: {SIDE[current]}{lastOn.get(current) ? ` (${ukDate(lastOn.get(current) as string)})` : ''}</span>}
        <button type="button" id={`${id}-step-REFUSED`} className={B_SECONDARY_SM} disabled={!!busy} onClick={() => open('REFUSED')}>Record refused</button>
        <button type="button" id={`${id}-step-ADJOURNED`} className={B_SECONDARY_SM} disabled={!!busy} onClick={() => open('ADJOURNED')}>Record adjourned</button>
      </div>
      {pick && (
        // A clean white edit card (no fields on a tint).
        <div role="group" aria-label={`Record “${label(pick.stage)}”`} className="rounded-[12px] border border-casevo-border bg-casevo-surface px-[14px] py-[12px] flex flex-col gap-[10px]">
          <p id={`${id}-step-day`} className="m-0 text-[13px] font-semibold text-casevo-forest">Record “{label(pick.stage)}”</p>
          <div className="grid gap-[10px] sm:grid-cols-[auto_1fr] items-end">
            <div><span className={LABEL}>On</span><DateInput value={pick.on} onChange={(v) => setPick({ ...pick, on: v })} ariaLabel="Date of the step" allowFuture={false} /></div>
            <div><label className={LABEL} htmlFor={`${id}-note`}>Note (optional)</label><input id={`${id}-note`} className={INPUT} value={pick.note} maxLength={1000} onChange={(e) => setPick({ ...pick, note: e.target.value })} /></div>
          </div>
          <div className="flex items-center gap-[8px]">
            <button type="button" className={B_PRIMARY} disabled={!!busy || !pick.on} onClick={() => { void record() }}>{busy === 'stage' ? 'Saving…' : 'Record'}</button>
            <button type="button" className={B_SECONDARY} onClick={cancelStep}>Cancel</button>
            {!pick.on && <span className={NOTE}>Enter the date first.</span>}
          </div>
        </div>
      )}
      {history.length > 0 && (
        <div className="text-[12px] text-casevo-text-soft">
          <span className="font-semibold">History</span>
          <ol aria-label="Court step history" className="m-0 mt-[2px] pl-[18px]">
            {history.map((h, i) => <li key={`${h.stage}-${i}`}>{ukDate(h.on)} — {label(h.stage)}{h.note ? ` (${h.note})` : ''}</li>)}
          </ol>
        </div>
      )}
    </div>
  )
}
